import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getDD, getListByDD } from 'y4/Y4Api';
import { getQueries } from 'y4/Utils';
import y, { textAlignToClass, formatByNamedArgs, removeUi } from 'y4/Utils';
import { run as runCommand } from '../Executer';
import _ from 'lodash';
import ActionButton from 'components/common/ActionButton';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EditGridPanel from './EditGridPanel';

/** Load all data from back end for DataPanel
 * @param {*} panel Data panel of Y4
 * @param {*} withDd false: Without Data defination
 * @param {*} queries
 * @returns
 */
async function loadData(panel, withDd = true, queries = {}) {
  const dataCode = panel.dataCode;

  const buttonClick = (e, rows) => {
    if (panel.buttonClick) panel.buttonClick(e, rows);
  };

  //Get data definition
  let rt_dd = null;
  if (withDd) {
    rt_dd = await getDD(dataCode, queries);
    if (rt_dd.isNotOk()) return { rt: rt_dd };
  }

  //Get all list and statistics by data definitioin
  const rt_data = await getListByDD(dataCode, {
    count: -1,
    statistics: 'false',
    ...queries
  });
  if (rt_data.isNotOk()) return { rt: rt_data };

  let columns =
    rt_dd &&
    rt_dd.data.fields
      .filter(f => !f.hidden)
      .map(f => {
        //columns that do some convert
        const c = {
          accessor: f.name + 'Ui',
          Header: f.title,
          cellProps: {
            className: textAlignToClass(f.textAlign)
          },
          headerProps: {
            className: textAlignToClass(f.captionAlign)
          }
        };
        if (f.width) {
          c.width = f.width;
        }
        if (f.readonly) {
          c.Cell = rowData => {
            return <>{rowData.row.original[f.name + 'Ui']}</>;
          };
        }
        return c;
      });

  //Add action columns
  if (!_.isEmpty(panel.buttons)) {
    columns = _.concat(columns, {
      accessor: 'none',
      Header: '',
      disableSortBy: true,
      with: 50,
      cellProps: {
        className: 'text-end py-2'
      },
      Cell: rowData => {
        const row_data = removeUi(rowData.data[rowData.row.index]);
        return (
          <>
            {panel.buttons
              .filter(item => {
                return item.type == 'row';
              })
              .map((button, index) => {
                const button_visible =
                  _.isEmpty(button.condition) ||
                  eval(
                    formatByNamedArgs(button.condition, k => {
                      return row_data[k];
                    })
                  );
                return button_visible ? (
                  <ActionButton
                    key={index}
                    icon={button.icon}
                    title={button.text}
                    onClick={() => buttonClick(button, [row_data])}
                    variant="action"
                    className="p-0 me-2"
                  />
                ) : null;
              })}
          </>
        );
      }
    });
  }
  //Return async data
  return {
    rt: rt_data, //last rt
    filters: rt_dd && rt_dd.data.queries,
    columns: columns,
    rows: rt_data.data.list, //rows
    statistics: rt_data.data.statistics //statistics
  };
}

/**
 *
 * @param {*} panel
 * @returns
 */
const EditGrid = ({ form, panel }) => {
  const { t } = useTranslation('data');
  const navigate = useNavigate();
  const location = useLocation();
  var queries = getQueries(location.search);
  //Data to show by state
  const [data, setData] = useState({
    error: 1,
    errorTitle: t('Loading....'),
    errorDescription: t('Data is still loading, please wait')
  });

  const [embedBadges, setEmbedBadges] = useState();

  //Load data after UI shown
  useEffect(() => {
    //Default filters
    const dfs = _.get(panel, 'option.defaultFilters', {});
    setFilter(dfs);

    const all_filter = { ...dfs, ...queries };
    //Load data from back end
    loadData(panel, true, all_filter).then(
      ({ rt, filters, columns, rows, statistics }) => {
        if (rt.isOk()) {
          panel.loadedColumns = columns;
          panel.loadedFilters = filters;
          setDataAndDispatch({ columns, rows, statistics, filters });
        } else {
          setData({
            error: 1,
            errorTitle: `Error ${rt.id}` + (rt.code ? `(${rt.code})` : ''),
            errorDescription: rt.message
          });
        }
      }
    );
  }, []);

  //Search data
  const [filter, setFilter] = useState();

  const search = fs => {
    setFilter(fs);
    loadData(panel, false, fs).then(({ rt, rows, statistics }) => {
      panel.currentFilters = fs;
      if (rt.isOk()) {
        setData({
          columns: panel.loadedColumns,
          filters: panel.loadedFilters,
          rows,
          statistics
        });
      } else {
        setData({
          error: 1,
          errorTitle: `Error ${rt.id}` + (rt.code ? `(${rt.code})` : ''),
          errorDescription: rt.message
        });
      }
    });
  };

  //Buttons click
  const buttonClick = (button, rows) => {
    runCommand({
      form,
      panel,
      command: button.command,
      params: button.params,
      javascript: button.javascript,
      dataCode: panel.dataCode,
      successMessage: button.successMessage,
      successCommand: button.successCommand,
      navigate: navigate,
      rows: rows,
      filter: filter,
      queries: queries
    });
  };

  //Row button click to use this function
  panel.buttonClick = buttonClick;

  //Dispatch afterPanelLoaded;
  const setDataAndDispatch = d => {
    setData(d);
    form.eventTarget.dispatchEvent(
      new CustomEvent('form-event', {
        detail: {
          event: 'afterPanelLoaded',
          panel,
          form,
          data: d
        }
      })
    );
  };

  //Listen events
  useEffect(() => {
    form.eventTarget.addEventListener('form-event', e => {
      const event = e.detail.event;
      const path = 'events.listen' + y.firstCharToUpper(event);
      const script = y.gpv(panel, path);
      if (script) {
        const f = Function('y', 'source', 'target', script);
        f(y, e.detail, {
          form,
          panel,
          data,
          setData,
          setDataAndDispatch,
          search,
          embedBadges,
          setEmbedBadges
        });
      }
    });
  }, [form.eventTarget]);

  return (
    data &&
    (data.error ? (
      <>
        <h5 className="mb-0 mt-1">{data.errorTitle}</h5>
        <div className="pt-3">{data.errorDescription}</div>
      </>
    ) : (
      <EditGridPanel
        form={form}
        panel={panel}
        columns={data.columns}
        rows={data.rows}
        buttonClick={buttonClick}
        embedBadges={embedBadges}
      ></EditGridPanel>
    ))
  );
};

EditGrid.propTypes = {
  form: PropTypes.object,
  panel: PropTypes.object
};

export default EditGrid;
